import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthSession } from '@/features/auth/useAuthSession';
import {
  countNotificationChannels,
  fetchNotificationPreferences,
  saveNotificationCategories,
} from '@/services/notifications';
import type { NotificationConsent } from '@/types/notifications';

/**
 * Настройки уведомлений текущего пользователя.
 *
 * Согласие принадлежит человеку, поэтому клиент здесь пишет напрямую под
 * RLS — в отличие от кредитов и наград, где решает сервер. Число каналов
 * читается рядом: без него экран не может честно сказать, дойдёт ли
 * уведомление вообще.
 */
export function useNotificationSettings() {
  const { status, session } = useAuthSession();
  const queryClient = useQueryClient();
  const userId = session?.user.id ?? null;

  const preferences = useQuery({
    queryKey: ['notification-preferences', userId],
    queryFn: fetchNotificationPreferences,
    enabled: status === 'ready',
  });

  const channels = useQuery({
    queryKey: ['notification-channels', userId],
    queryFn: countNotificationChannels,
    enabled: status === 'ready',
  });

  const mutation = useMutation({
    mutationFn: (categories: NotificationConsent) => {
      if (!userId) {
        return Promise.reject(new Error('Нет активной сессии. Обновите страницу.'));
      }

      return saveNotificationCategories(userId, categories);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  return {
    preferences: preferences.data ?? null,
    channels: channels.data ?? 0,
    isPending: preferences.isPending,
    isError: preferences.isError,
    refetch: () => void preferences.refetch(),
    save: (categories: NotificationConsent) => mutation.mutate(categories),
    saveStatus: mutation.status,
    saveError: mutation.error?.message ?? null,
  };
}
